import { useState, useMemo, useCallback } from "react";
import type { ModelInfo } from "@shared/schemas/models";

export interface ProviderGroup {
  provider: string;
  models: ModelInfo[];
}

export function useModelSelector(models: ModelInfo[]) {
  const [expandedProvider, setExpandedProvider] = useState<string | null>(null);

  const providerGroups: ProviderGroup[] = useMemo(() => {
    const map = new Map<string, ModelInfo[]>();
    for (const model of models) {
      const existing = map.get(model.provider) ?? [];
      existing.push(model);
      map.set(model.provider, existing);
    }
    return Array.from(map.entries()).map(([provider, models]) => ({
      provider,
      models,
    }));
  }, [models]);

  const toggleProvider = useCallback((provider: string) => {
    setExpandedProvider((prev) => (prev === provider ? null : provider));
  }, []);

  const expandProviderForModel = useCallback((modelId: string) => {
    const group = providerGroups.find((g) => g.models.some((m) => m.id === modelId));
    setExpandedProvider(group && group.models.length > 1 ? group.provider : null);
  }, [providerGroups]);

  const collapseAll = useCallback(() => {
    setExpandedProvider(null);
  }, []);

  return { providerGroups, expandedProvider, toggleProvider, expandProviderForModel, collapseAll };
}
